import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { useAuth } from '../context/AuthContext';
import { DashboardSkeleton } from './LoadingSkeleton';
import { debug } from '../utils/debug';

// How long the native splash screen is shown before transitioning to skeleton
// while waiting for Firebase Auth to resolve on a slow network (e.g. ship WiFi).
// AUTH_STATE_TIMEOUT_MS (AuthContext) is the total budget; this is the splash slice.
// Timeline on poor network: 0-3s splash → 3-5s skeleton → 5s auth timeout → login screen.
const SPLASH_MAX_DURATION_MS = 3000;

const InitialRouteHandler = () => {
  const { currentUser, loading } = useAuth();
  const [initialRouteDetermined, setInitialRouteDetermined] = useState(false);
  const [redirectTo, setRedirectTo] = useState<string | null>(null);
  const [splashTimeExpired, setSplashTimeExpired] = useState(false);
  const isNative = Capacitor.getPlatform() !== 'web';

  // On native platforms: hide the splash and show a skeleton after SPLASH_MAX_DURATION_MS
  // if auth hasn't resolved yet. Prevents the branded splash from feeling "frozen".
  useEffect(() => {
    if (!isNative) return;
    const splashTimer = setTimeout(() => {
      setSplashTimeExpired(true);
    }, SPLASH_MAX_DURATION_MS);
    return () => clearTimeout(splashTimer);
  }, [isNative]);

  useEffect(() => {
    // Only proceed once authentication status is determined
    if (loading) return;

    if (currentUser) {
      // User is authenticated, go to dashboard
      setRedirectTo('/dashboard');
    } else {
      // User is not authenticated, check visit history
      const hasVisitedBefore = localStorage.getItem('hasVisitedBefore');
      
      if (!hasVisitedBefore) {
        // First-time visitor - go to signup (don't mark as visited until they actually sign up)
        setRedirectTo('/signup');
      } else {
        // Returning visitor - go to login
        setRedirectTo('/login');
      }
    }

    setInitialRouteDetermined(true);
  }, [currentUser, loading]);

  // Hide splash screen when navigating to unauthenticated routes (signup/login)
  // For authenticated routes, the DashboardSkeleton will handle hiding the splash
  useEffect(() => {
    if (initialRouteDetermined && redirectTo && isNative && !currentUser) {
      // Hide splash screen right before navigating to auth pages
      // This ensures smooth transition from splash directly to signup/login
      SplashScreen.hide();
    }
  }, [initialRouteDetermined, redirectTo, isNative, currentUser]);

  // While loading or determining route, show appropriate loading state
  if (!initialRouteDetermined || loading) {
    if (currentUser) {
      // Authenticated user - show DashboardSkeleton for seamless transition
      debug.log('[InitialRouteHandler] Showing DashboardSkeleton - authenticated user');
      if (isNative) {
        SplashScreen.hide();
      }
      return <DashboardSkeleton />;
    } else if (splashTimeExpired && isNative) {
      // Splash has been visible for SPLASH_MAX_DURATION_MS — transition to skeleton
      // so the screen doesn't look frozen while auth is still resolving.
      // The auth timeout in AuthContext will fire within 2s of this point.
      debug.log('[InitialRouteHandler] Splash timer expired - showing skeleton while awaiting auth');
      SplashScreen.hide();
      return <DashboardSkeleton />;
    } else {
      // Keep native splash screen visible (still within the 3s window) or show nothing on web
      debug.log('[InitialRouteHandler] Keeping splash screen visible - unauthenticated user');
      return null;
    }
  }

  // Navigate to determined route
  // At this point, initialRouteDetermined is true and loading is false
  if (redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }

  // Fallback (should not be reached with correct logic)
  return null;
};

export default InitialRouteHandler;